//frontend/dispatcher-client/src/components/CreateInvoice.js
import React, { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import { customSelectStyles } from "../styles/reactSelectStyles.js";
import { FaRegCalendarAlt, FaRegClock } from "react-icons/fa";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import "../styles/CreateInvoice.css";
import { useTranslation } from "react-i18next";

export default function CreateInvoice() {
  const { t } = useTranslation("create_invoice");
  const navigate = useNavigate();

  const issueDateRef = useRef(null);

  const MAX_POINTS = 7;

  const emptyPoint = () => ({
    location: "",
    date: "",
    hour: "",
  });

  const [pickupPoints, setPickupPoints] = useState([emptyPoint()]);
  const [deliveryPoints, setDeliveryPoints] = useState([emptyPoint()]);

  const [invoiceId] = useState("INV-" + Date.now());

  const [clientOption, setClientOption] = useState(null);
  const [status, setStatus] = useState("");

  const [price, setPrice] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [expiryOption, setExpiryOption] = useState(null);

  const [clientOptions, setClientOptions] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchClients = async () => {
      try {
        setIsLoadingClients(true);

        const [industriesRes, carriersRes] = await Promise.all([
          axios.get("/industries"),
          axios.get("/carriers"),
        ]);

        const industries = Array.isArray(industriesRes?.data) ? industriesRes.data : [];
        const carriers = Array.isArray(carriersRes?.data) ? carriersRes.data : [];

        const industryOptions = industries
          .map((item) => {
            const name =
              item?.name ||
              item?.industryName ||
              item?.companyName ||
              item?.clientName ||
              "";

            if (!String(name).trim()) return null;

            return {
              value: String(name).trim(),
              label: String(name).trim(),
              clientType: "industry",
              clientTypeLabel: t("client_type.Industry", { defaultValue: "Industry" }),
              industryClientId: item?.id ?? null,
              carrierClientId: null,
              iva: item?.iva ?? null,
              vatRate: item?.iva ?? null,
            };
          })
          .filter(Boolean);

        const carrierOptions = carriers
          .map((item) => {
            const name =
              item?.name ||
              item?.carrierName ||
              item?.companyName ||
              item?.clientName ||
              "";

            if (!String(name).trim()) return null;

            return {
              value: String(name).trim(),
              label: String(name).trim(),
              clientType: "carrier",
              clientTypeLabel: t("client_type.Carrier", { defaultValue: "Carrier" }),
              industryClientId: null,
              carrierClientId: item?.id ?? null,
              iva: item?.iva ?? null,
              vatRate: item?.iva ?? null,
            };
          })
          .filter(Boolean);

        const merged = [...industryOptions, ...carrierOptions];

        const uniqueMap = new Map();
        for (const opt of merged) {
          const key = `${opt.label.toLowerCase()}__${opt.clientType}`;
          if (!uniqueMap.has(key)) uniqueMap.set(key, opt);
        }

        const sorted = Array.from(uniqueMap.values()).sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
        );

        if (!cancelled) setClientOptions(sorted);
      } catch (err) {
        console.error("❌ Failed to fetch invoice clients:", err);
        if (!cancelled) setClientOptions([]);
      } finally {
        if (!cancelled) setIsLoadingClients(false);
      }
    };

    fetchClients();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const normalizeHourInput = (value) => {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 4);
    if (!digits) return "";
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  };

  const updatePoint = (type, index, field, value) => {
    const setter = type === "pickup" ? setPickupPoints : setDeliveryPoints;

    setter((prev) =>
      prev.map((point, i) =>
        i === index ? { ...point, [field]: value } : point
      )
    );
  };

  const addPoint = (type) => {
    const setter = type === "pickup" ? setPickupPoints : setDeliveryPoints;

    setter((prev) => {
      if (prev.length >= MAX_POINTS) return prev;
      return [...prev, emptyPoint()];
    });
  };

  const removePoint = (type, index) => {
    const setter = type === "pickup" ? setPickupPoints : setDeliveryPoints;

    setter((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });

    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${type}Location-${index}`];
      delete next[`${type}Date-${index}`];
      delete next[`${type}Hour-${index}`];
      return next;
    });
  };

  const reqLabel = (label, plural = false) =>
    plural
      ? t("validation.required_plural", { field: label })
      : t("validation.required_singular", { field: label });

  const normalizeVatRate = (value) => {
    if (value === undefined || value === null || value === "") return null;
  
    const normalized = String(value).replace(",", ".").trim();
    const parsed = Number(normalized);
  
    if (!Number.isFinite(parsed)) return null;
  
    return Math.min(100, Math.max(0, parsed));
  };

  const validate = () => {
    const next = {};

    if (!clientOption?.label?.trim()) next.clientName = reqLabel(t("fields.client_name"));
    if (!status?.value) next.status = reqLabel(t("fields.status"));

    pickupPoints.forEach((point, index) => {
      if (!point.location?.trim()) {
        next[`pickupLocation-${index}`] = reqLabel(t("fields.pickup_place"));
      }
      if (!point.date) {
        next[`pickupDate-${index}`] = reqLabel(t("fields.pickup_date") || "Pickup date");
      }
      if (!point.hour?.trim()) {
        next[`pickupHour-${index}`] = reqLabel(t("fields.pickup_hour") || "Pickup hour");
      }
    });

    deliveryPoints.forEach((point, index) => {
      if (!point.location?.trim()) {
        next[`deliveryLocation-${index}`] = reqLabel(t("fields.delivery_place"));
      }
      if (!point.date) {
        next[`deliveryDate-${index}`] = reqLabel(t("fields.delivery_date") || "Delivery date");
      }
      if (!point.hour?.trim()) {
        next[`deliveryHour-${index}`] = reqLabel(t("fields.delivery_hour") || "Delivery hour");
      }
    });

    const pNorm =
      typeof price === "string"
        ? price.replace(",", ".").trim()
        : String(price ?? "").trim();

    if (!pNorm) next.price = reqLabel(t("fields.price"));
    else if (!Number.isFinite(Number(pNorm))) {
      next.price = t("validation.invalid_number", { field: t("fields.price") });
    }

    if (!issueDate) next.issueDate = reqLabel(t("fields.issue_date"));
    if (!expiryOption?.value) {
      next.expiryOption = reqLabel(t("fields.payment_expiration"), true);
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const statusOptions = useMemo(
    () => [
      { value: "DRAFT", label: t("status.DRAFT") },
      { value: "SENT", label: t("status.SENT") },
      { value: "PARTIAL", label: t("status.PARTIAL") },
      { value: "PAID", label: t("status.PAID") },
      { value: "VOID", label: t("status.VOID") },
    ],
    [t]
  );

  const expiryOptions = useMemo(
    () => [30, 45, 60, 90].map((d) => ({ value: d, label: t("expiry.days", { count: d }) })),
    [t]
  );

  const clientSelectStyles = useMemo(() => {
    return {
      ...customSelectStyles,
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? "#232937" : "#1b1f26",
        color: "#e8f0ff",
        paddingTop: 10,
        paddingBottom: 10,
        cursor: "pointer",
      }),
      singleValue: (base) => ({
        ...base,
        color: "#e8f0ff",
      }),
    };
  }, []);

  const formatClientOptionLabel = (option, { context }) => {
    if (context === "value") {
      return <span>{option.label}</span>;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <span style={{ fontWeight: 600 }}>{option.label}</span>
        {option.clientTypeLabel ? (
          <span style={{ fontSize: "11px", opacity: 0.75, marginTop: 2 }}>
            {option.clientTypeLabel}
          </span>
        ) : null}
      </div>
    );
  };

  const handleCreateClientOption = (inputValue) => {
    const trimmed = String(inputValue || "").trim();
    if (!trimmed) return;

    const newOption = {
      value: trimmed,
      label: trimmed,
      clientType: null,
      clientTypeLabel: null,
      industryClientId: null,
      carrierClientId: null,
      iva: null,
      vatRate: null,
      __isNew__: true,
    };

    setClientOption(newOption);
    clearError("clientName");
  };

  const handleCreate = async () => {
    if (!validate()) return;

    try {
      const expiryDays =
        typeof expiryOption?.value === "number"
          ? expiryOption.value
          : parseInt(expiryOption.value, 10);

      const d = new Date(issueDate);
      d.setDate(d.getDate() + expiryDays);
      const paymentDueDate = d.toISOString().slice(0, 10);

      const priceNormalized =
        typeof price === "string" ? price.replace(",", ".").trim() : String(price).trim();

      const cleanedPickupPoints = pickupPoints.map((point) => ({
        location: point.location.trim(),
        date: point.date,
        hour: point.hour.trim(),
      }));

      const cleanedDeliveryPoints = deliveryPoints.map((point) => ({
        location: point.location.trim(),
        date: point.date,
        hour: point.hour.trim(),
      }));

      const selectedClientVatRate = normalizeVatRate(
        clientOption?.vatRate ?? clientOption?.iva
      );
      
      const payload = {
        client: clientOption.label.trim(),
        clientType: clientOption?.clientType ?? null,
        industryClientId:
          clientOption?.clientType === "industry"
            ? clientOption?.industryClientId ?? null
            : null,
        carrierClientId:
          clientOption?.clientType === "carrier"
            ? clientOption?.carrierClientId ?? null
            : null,
        vatRate: selectedClientVatRate,
        issueDate,
        expiryDays,
        paymentDueDate,

        hour: null,
        transportsessionId: null,
        loadId: null,

        // compatibilidade com backend atual
        pickup: cleanedPickupPoints[0]?.location || null,
        pickupDate: cleanedPickupPoints[0]?.date || null,
        pickupHour: cleanedPickupPoints[0]?.hour || null,

        delivery: cleanedDeliveryPoints[cleanedDeliveryPoints.length - 1]?.location || null,
        deliveryDate: cleanedDeliveryPoints[cleanedDeliveryPoints.length - 1]?.date || null,
        deliveryHour: cleanedDeliveryPoints[cleanedDeliveryPoints.length - 1]?.hour || null,

        // novo formato
        pickupCount: cleanedPickupPoints.length,
        deliveryCount: cleanedDeliveryPoints.length,
        pickupList: cleanedPickupPoints,
        deliveryList: cleanedDeliveryPoints,

        pickupLocation: cleanedPickupPoints[0]?.location || null,
        deliveryLocation: cleanedDeliveryPoints[cleanedDeliveryPoints.length - 1]?.location || null,

        price: String(priceNormalized),
        status: status.value,
      };

      await axios.post("/invoices", payload);

      console.log("[CreateInvoice] payload VAT debug =", {
        selectedClient: clientOption,
        selectedClientVatRate,
        payloadVatRate: payload.vatRate,
        payloadClientType: payload.clientType,
        payloadIndustryClientId: payload.industryClientId,
        payloadCarrierClientId: payload.carrierClientId,
      });

      setErrorMessage("");
      setShowSuccessPopup(true);

      setTimeout(() => {
        navigate("/invoices");
      }, 1200);
    } catch (err) {
      console.error("❌ Failed to create invoice:", err);
      setShowSuccessPopup(false);
      setErrorMessage(
        err.response?.data?.error ||
          t("messages.error_create", {
            defaultValue: "Falha ao criar o documento de cobrança.",
          })
      );
    }
  };

  return (
    <div className="create-invoice-container">
      <div className="create-invoice-back-arrow" onClick={() => navigate("/invoices")}>
        ← {t("page.back")}
      </div>

      <div className="create-invoice-header">
        <h2 className="create-invoice-title">{t("page.title")}</h2>
      </div>

      {errorMessage && (
        <p className="create-invoice-error-message">{errorMessage}</p>
      )}

      {showSuccessPopup && (
        <div className="create-invoice-successPopup-overlay">
          <div className="create-invoice-successPopup">
            <div className="create-invoice-successPopup-icon">✓</div>

            <h3>
              {t("success.createdTitle", {
                defaultValue: "Documento criado",
              })}
            </h3>

            <p>
              {t("success.created", {
                defaultValue: "Documento de cobrança criado com sucesso.",
              })}
            </p>

            <button
              type="button"
              onClick={() => navigate("/invoices")}
              className="create-invoice-successPopup-btn"
            >
              {t("actions.ok", {
                defaultValue: "OK",
              })}
            </button>
          </div>
        </div>
      )}

      <div className="create-invoice-form">
        <div className="create-invoice-form-top">
          <div className="create-invoice-id">
            <span className="invoice-id-label">{t("fields.invoice_id")}</span>
            <span className="invoice-id-value">{invoiceId}</span>
          </div>
        </div>

        <div className="create-invoice-form-row create-invoice-form-row--two">
          <div className="create-invoice-field">
            <label className="create-invoice-label">{t("fields.client_name")}*</label>
            <div className={`create-invoice-select ${errors.clientName ? "has-error" : ""}`}>
              <CreatableSelect
                classNamePrefix="react-select"
                options={clientOptions}
                value={clientOption}
                onChange={(opt) => {
                  setClientOption(opt);
                  if (opt?.label?.trim()) clearError("clientName");
                }}
                onCreateOption={handleCreateClientOption}
                placeholder={t("placeholders.client_name")}
                styles={clientSelectStyles}
                formatOptionLabel={formatClientOptionLabel}
                isClearable
                isLoading={isLoadingClients}
              />
            </div>
            {errors.clientName && (
              <div className="create-invoice-error create-invoice-error--select">
                {errors.clientName}
              </div>
            )}
          </div>

          <div className="create-invoice-field">
            <label className="create-invoice-label">{t("fields.status")}*</label>
            <div className={`create-invoice-select ${errors.status ? "has-error" : ""}`}>
              <Select
                classNamePrefix="react-select"
                options={statusOptions}
                value={status}
                onChange={(opt) => {
                  setStatus(opt);
                  if (opt?.value) clearError("status");
                }}
                placeholder={t("placeholders.status")}
                styles={customSelectStyles}
                isClearable
              />
            </div>
            {errors.status && (
              <div className="create-invoice-error create-invoice-error--select">
                {errors.status}
              </div>
            )}
          </div>
        </div>

        <div className="create-invoice-form-row create-invoice-form-row--two">
          <div className="create-invoice-field">
            <label className="create-invoice-label">{t("fields.pickup_place")}*</label>

            {pickupPoints.map((point, index) => (
              <div key={`pickup-${index}`} className="create-invoice-point-card">
                <input
                  className={`create-invoice-input ${errors[`pickupLocation-${index}`] ? "input-error" : ""}`}
                  type="text"
                  value={point.location}
                  onChange={(e) => {
                    const v = e.target.value;
                    updatePoint("pickup", index, "location", v);
                    if (v?.trim()) clearError(`pickupLocation-${index}`);
                  }}
                  placeholder={t("placeholders.pickup_place")}
                />
                {errors[`pickupLocation-${index}`] && (
                  <div className="create-invoice-error">{errors[`pickupLocation-${index}`]}</div>
                )}

                <div className="create-invoice-inline-datetime">
                  <div className="create-invoice-inline-date">
                    <div className="create-invoice-input-with-icon create-invoice-input-with-icon--compact">
                      <input
                        className={`create-invoice-input ${errors[`pickupDate-${index}`] ? "input-error" : ""}`}
                        type="date"
                        value={point.date}
                        onPointerDown={(e) => {
                          try {
                            e.currentTarget.showPicker?.();
                          } catch (_) {}
                        }}
                        onChange={(e) => {
                          const v = e.target.value;
                          updatePoint("pickup", index, "date", v);
                          if (v) clearError(`pickupDate-${index}`);
                        }}
                      />
                      <FaRegCalendarAlt className="create-invoice-calendar-icon" />
                    </div>
                    {errors[`pickupDate-${index}`] && (
                      <div className="create-invoice-error">{errors[`pickupDate-${index}`]}</div>
                    )}
                  </div>

                  <div className="create-invoice-inline-time">
                    <div className="create-invoice-input-with-icon create-invoice-input-with-icon--compact">
                      <input
                        className={`create-invoice-input ${errors[`pickupHour-${index}`] ? "input-error" : ""}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        value={point.hour}
                        onChange={(e) => {
                          const v = normalizeHourInput(e.target.value);
                          updatePoint("pickup", index, "hour", v);
                          if (v?.trim()) clearError(`pickupHour-${index}`);
                        }}
                        placeholder="HH:MM"
                      />
                      <FaRegClock className="create-invoice-calendar-icon" />
                    </div>
                    {errors[`pickupHour-${index}`] && (
                      <div className="create-invoice-error">{errors[`pickupHour-${index}`]}</div>
                    )}
                  </div>
                </div>

                {pickupPoints.length > 1 && (
                  <button
                    type="button"
                    className="create-invoice-remove-point-btn"
                    onClick={() => removePoint("pickup", index)}
                  >
                    {t("buttons.remove") || "Remove"}
                  </button>
                )}
              </div>
            ))}

            {pickupPoints.length < MAX_POINTS && (
              <button
                type="button"
                className="create-invoice-add-point-btn"
                onClick={() => addPoint("pickup")}
              >
                + {t("buttons.add_another_pickup") || "Adicionar outra Recolha"}
              </button>
            )}
          </div>

          <div className="create-invoice-field">
            <label className="create-invoice-label">{t("fields.delivery_place")}*</label>

            {deliveryPoints.map((point, index) => (
              <div key={`delivery-${index}`} className="create-invoice-point-card">
                <input
                  className={`create-invoice-input ${errors[`deliveryLocation-${index}`] ? "input-error" : ""}`}
                  type="text"
                  value={point.location}
                  onChange={(e) => {
                    const v = e.target.value;
                    updatePoint("delivery", index, "location", v);
                    if (v?.trim()) clearError(`deliveryLocation-${index}`);
                  }}
                  placeholder={t("placeholders.delivery_place")}
                />
                {errors[`deliveryLocation-${index}`] && (
                  <div className="create-invoice-error">{errors[`deliveryLocation-${index}`]}</div>
                )}

                <div className="create-invoice-inline-datetime">
                  <div className="create-invoice-inline-date">
                    <div className="create-invoice-input-with-icon create-invoice-input-with-icon--compact">
                      <input
                        className={`create-invoice-input ${errors[`deliveryDate-${index}`] ? "input-error" : ""}`}
                        type="date"
                        value={point.date}
                        onPointerDown={(e) => {
                          try {
                            e.currentTarget.showPicker?.();
                          } catch (_) {}
                        }}
                        onChange={(e) => {
                          const v = e.target.value;
                          updatePoint("delivery", index, "date", v);
                          if (v) clearError(`deliveryDate-${index}`);
                        }}
                      />
                      <FaRegCalendarAlt className="create-invoice-calendar-icon" />
                    </div>
                    {errors[`deliveryDate-${index}`] && (
                      <div className="create-invoice-error">{errors[`deliveryDate-${index}`]}</div>
                    )}
                  </div>

                  <div className="create-invoice-inline-time">
                    <div className="create-invoice-input-with-icon create-invoice-input-with-icon--compact">
                      <input
                        className={`create-invoice-input ${errors[`deliveryHour-${index}`] ? "input-error" : ""}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        value={point.hour}
                        onChange={(e) => {
                          const v = normalizeHourInput(e.target.value);
                          updatePoint("delivery", index, "hour", v);
                          if (v?.trim()) clearError(`deliveryHour-${index}`);
                        }}
                        placeholder="HH:MM"
                      />
                      <FaRegClock className="create-invoice-calendar-icon" />
                    </div>
                    {errors[`deliveryHour-${index}`] && (
                      <div className="create-invoice-error">{errors[`deliveryHour-${index}`]}</div>
                    )}
                  </div>
                </div>

                {deliveryPoints.length > 1 && (
                  <button
                    type="button"
                    className="create-invoice-remove-point-btn"
                    onClick={() => removePoint("delivery", index)}
                  >
                    {t("buttons.remove") || "Remove"}
                  </button>
                )}
              </div>
            ))}

            {deliveryPoints.length < MAX_POINTS && (
              <button
                type="button"
                className="create-invoice-add-point-btn"
                onClick={() => addPoint("delivery")}
              >
                + {t("buttons.add_another_delivery") || "Adicionar outra Descarga"}
              </button>
            )}
          </div>
        </div>

        <div className="create-invoice-form-row">
          <div className="create-invoice-field">
            <label className="create-invoice-label">{t("fields.price")}*</label>
            <input
              className={`create-invoice-input ${errors.price ? "input-error" : ""}`}
              type="number"
              value={price}
              onChange={(e) => {
                const v = e.target.value;
                setPrice(v);
                if (v?.trim()) clearError("price");
              }}
              placeholder={t("placeholders.price")}
              inputMode="decimal"
            />
            {errors.price && <div className="create-invoice-error">{errors.price}</div>}
          </div>

          <div className="create-invoice-field">
            <label className="create-invoice-label">{t("fields.issue_date")}*</label>
            <div className="create-invoice-input-with-icon">
              <input
                ref={issueDateRef}
                className={`create-invoice-input ${errors.issueDate ? "input-error" : ""}`}
                type="date"
                value={issueDate}
                onPointerDown={(e) => {
                  try {
                    e.currentTarget.showPicker?.();
                  } catch (_) {}
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  setIssueDate(v);
                  if (v) clearError("issueDate");
                }}
              />
              <FaRegCalendarAlt
                className="create-invoice-calendar-icon"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    issueDateRef.current?.showPicker?.();
                  } catch (_) {
                    issueDateRef.current?.focus?.();
                  }
                }}
                title={t("tooltips.open_calendar")}
              />
            </div>
            {errors.issueDate && <div className="create-invoice-error">{errors.issueDate}</div>}
          </div>

          <div className="create-invoice-field">
            <label className="create-invoice-label">{t("fields.payment_expiration")}*</label>
            <div className={`create-invoice-select ${errors.expiryOption ? "has-error" : ""}`}>
              <Select
                classNamePrefix="react-select"
                options={expiryOptions}
                value={expiryOption}
                onChange={(opt) => {
                  setExpiryOption(opt);
                  if (opt?.value) clearError("expiryOption");
                }}
                placeholder={t("placeholders.payment_expiration")}
                styles={customSelectStyles}
                isClearable
              />
            </div>
            {errors.expiryOption && (
              <div className="create-invoice-error create-invoice-error--select">
                {errors.expiryOption}
              </div>
            )}
          </div>
        </div>

        <div className="create-invoice-buttons-row">
          <button
            className="create-invoice-btn create-invoice-cancel-btn"
            onClick={() => navigate("/invoices")}
          >
            {t("buttons.cancel")}
          </button>
          <button
            className="create-invoice-btn create-invoice-submit-btn"
            onClick={handleCreate}
          >
            {t("buttons.create_invoice")}
          </button>
        </div>
      </div>
    </div>
  );
}